import { Component, OnInit, forwardRef, Injector, Input, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  AbstractControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { BaseComponent } from '../base-component/base.component';
import { LanguageEnum } from 'src/app/core/common';
import { IconSvgComponent } from '../icon/svg-icon.component';
import { IconsConstants } from '../../common';
import { SvgIcon } from '../../interfaces';
import { IconFromFileExtensionPipe } from '../../pipes/icon-file-extension-pipe';

@Component({
  selector: 'app-file-upload-control',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatIconModule,
    IconSvgComponent,
    IconFromFileExtensionPipe,
  ],
  templateUrl: './file-upload-control.component.html',
  styleUrls: ['./file-upload-control.component.scss'],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => FileUploadControlComponent), multi: true }],
})
export class FileUploadControlComponent extends BaseComponent implements OnInit, ControlValueAccessor {
  form!: FormGroup;
  onChanged = (file: File[]) => {};
  onTouched: any;
  filesList: File[] = [];
  data: DataTransfer = new DataTransfer();

  //inputs
  @Input() isMultipleFiles: boolean = true;
  @Input() accept: string;
  @Input() label: string = this.translationsList['BUTTONS']['CHOOSE_FILE'];
  @Input() icon: SvgIcon = IconsConstants.FOLDER_CLOUD;
  @Input() isButtonElement: boolean = false;

  //references
  @ViewChild('input') fileInputRef: ElementRef;

  //constants
  IconsConstants = IconsConstants;

  // getters
  get files(): AbstractControl {
    return this.form.get('files');
  }
  constructor(injector: Injector) {
    super(['BUTTONS'], injector);
  }
  writeValue(obj: any): void {
    this.filesList = obj ?? [];
    this.files.setValue(this.filesList);
  }
  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  ngOnInit(): void {
    this.initializeForm();
  }
  formatBytes(bytes: number, decimals = 2): string {
    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const dm = decimals <= 0 ? 0 : decimals || 2;
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    if (this.language == LanguageEnum.Arabic) {
      const sizes = ['بايت', 'كيلو بايت', 'ميغا بايت', 'جيجا بايت', 'تيرا بايت'];
      return (bytes / Math.pow(k, i)).toLocaleString('ar-EG', { maximumFractionDigits: 2 }) + ' ' + sizes[i];
    } else {
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
      return (bytes / Math.pow(k, i)).toLocaleString('en-US', { maximumFractionDigits: 2 }) + ' ' + sizes[i];
    }
  }
  onChangeFiles(files, input: HTMLInputElement): void {
    const filesArray: File[] = [];
    if (files.files.length > 0) {
      for (const file of files.files as File[]) {
        file['fileSizeFormat'] = this.formatBytes(file.size);
        filesArray.push(file);
        this.data.items.add(file);
      }
      this.filesList = filesArray;
      this.files.setValue(this.filesList);
      this.onChanged(this.filesList);
      input.files = this.data.files;
    }
  }
  onTouch(event: Event) {
    this.onTouched();
  }

  onDelete(fileIndex: number, input: HTMLInputElement): void {
    this.filesList.splice(fileIndex, 1);
    this.files.setValue(this.filesList);
    this.onChanged(this.filesList);
    this.data.items.remove(fileIndex);
    input.files = this.data.files;
  }

  onClickLabel(inputField: HTMLElement): void {
    inputField.click();
  }

  // privates

  private initializeForm(): void {
    this.form = new FormGroup({
      files: new FormControl([]),
    });
  }
}
